import React, { useImperativeHandle, useRef } from 'react'
import { cn } from 'cn'
import { ExclamationCircleIcon } from '@heroicons/react/24/solid'
import FieldDescription from 'components/ui/FieldDescription'
import FieldError from 'components/ui/FieldError'

export type Props = {
  name: string
  label?: React.ReactElement | string
  autoComplete?: string
  description?: string | React.ReactElement | null
  placeholder?: string
  error?: string | null
  fullWidth?: boolean
  forwardedRef?: React.Ref<HTMLInputElement>
  children?: React.ReactElement | string | null
  iconLeft?: React.ReactElement<React.HTMLAttributes<HTMLElement>>
  iconRight?: React.ReactElement<React.HTMLAttributes<HTMLElement>>
  inputAdornment?: string
  inputRightAdornment?: string
  prefix?: React.ReactNode
  step?: string
  labelSize?: 'small' | 'base'
  newDesign?: boolean
} & Omit<
  React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >,
  'ref' | 'prefix'
> & { ref?: React.Ref<HTMLInputElement> }

const TextField = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      label,
      name,
      description,
      placeholder,
      error,
      type,
      fullWidth = true,
      iconLeft,
      iconRight,
      inputAdornment,
      inputRightAdornment,
      prefix,
      children,
      labelSize = 'base',
      autoComplete,
      newDesign = false,
      ...inputProps
    },
    forwardedRef
  ) => {
    const inputRef = useRef<HTMLInputElement>(null)
    useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(
      forwardedRef,
      () => inputRef.current
    )

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      inputRef?.current?.scrollIntoView({ block: 'nearest' })
      if (inputProps.onFocus) {
        inputProps.onFocus(e)
      }
    }

    const sharedInputProps = {
      ...inputProps,
      ref: inputRef,
      autoComplete,
      id: name,
      name,
      onFocus: handleFocus,
      placeholder,
      type: type || 'text'
    }

    return (
      <div className={cn('relative', { 'w-full': fullWidth })}>
        {label && (
          <label
            className={cn('block', {
              'text-sm font-medium text-gray-700':
                labelSize === 'small' && !newDesign,
              'text-base font-medium text-gray-700':
                !newDesign && labelSize !== 'small',
              'font-theme-heading font-bold text-gray-900': newDesign
            })}
            htmlFor={name}
          >
            {label}
          </label>
        )}
        <FieldDescription newDesign={newDesign}>{description}</FieldDescription>
        {prefix ? (
          <div
            className={cn(
              'mt-1 flex items-center border border-solid border-gray-300 rounded-xl shadow-xs bg-white',
              'focus-within:ring-1 focus-within:ring-orange-500 focus-within:border-orange-500',
              {
                'border-red-500 ring-1 ring-red-500': error,
                'w-full': fullWidth
              }
            )}
          >
            <span className="shrink-0 pl-3 text-gray-500 select-none text-sm flex items-center gap-2">
              {prefix}
            </span>
            <input
              {...sharedInputProps}
              className={cn(
                'block min-w-0 grow bg-transparent py-2 pr-3 pl-1 border-0 focus:ring-0 focus:outline-hidden focus:border-0 rounded-r-xl',
                {
                  'text-gray-500': inputProps.disabled
                },
                inputProps.className
              )}
            />
          </div>
        ) : (
          <div className="relative">
            {iconLeft && (
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                {/* eslint-disable-next-line react/no-clone-element */}
                {React.cloneElement(iconLeft, {
                  className: cn(
                    'h-5 w-5 text-gray-400',
                    iconLeft.props.className
                  ),
                  'aria-hidden': true
                })}
              </div>
            )}
            {inputAdornment && (
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 pt-1">{inputAdornment}</span>
              </div>
            )}
            <input
              {...sharedInputProps}
              className={cn(
                'mt-1 border border-solid border-gray-300 p-2 outline-none-mouse focus:ring-orange-500 focus:border-orange-500 shadow-xs rounded-xl scroll-offset',
                {
                  'text-gray-500': inputProps.disabled,
                  'ring-red-500 border-red-500': error,
                  'w-full': fullWidth,
                  'pl-10': !!iconLeft,
                  'pr-10': !!iconRight,
                  'pl-7 pr-12': inputAdornment
                },
                inputProps.className
              )}
            />
            {iconRight && (
              <div className="absolute inset-y-0 right-0 pr-3 pt-1 flex items-center pointer-events-none">
                {/* eslint-disable-next-line react/no-clone-element */}
                {React.cloneElement(iconRight, {
                  className: cn(
                    'h-5 w-5 text-gray-400',
                    iconRight.props.className
                  ),
                  'aria-hidden': true
                })}
              </div>
            )}
            {inputRightAdornment && inputRightAdornment}

            {error && (
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <ExclamationCircleIcon
                  aria-hidden="true"
                  className="h-5 w-5 text-red-500"
                />
              </div>
            )}
          </div>
        )}
        <FieldError>{error}</FieldError>
        {children}
      </div>
    )
  }
)

TextField.displayName = 'TextField'

export default TextField
